define((require) => {
    "use strict";

    var Marionette             = require("marionette"),
        EventDispatcher        = require("shared/util/EventDispatcher"),
        EditorCommentView      = require("editor/ui/experience/editor/EditorCommentView"),
        _                      = require("underscore"),
        Preconditions          = require("shared/util/Preconditions"),
        Comment                = require("ui/comments/Comment");

    var NEW_COMMENT_WIDTH = 213,
        SCENE_PADDING = 40;

    /**
     * The collection view of comments.
     *
     * @author Bruce Mackenzie Nielsen
     * @class CommentsView
     * @constructor
     *
     * @cfg {CommentCollection} collection The collection of comments to render.
     * @cfg {EditorViewModel} viewModel The editor view model.
     */
    var CommentsView = Marionette.CompositeView.extend({

        template: function() { return ""; },

        className: "editor-comments-container no-scale",

        itemView: EditorCommentView,

        _currentSceneID: null,

        itemEvents: {
            "commentViewClicked": "commentViewClickedHandler"
        },

        initialize: function() {
            this.itemViewOptions = {
                viewModel: this.options.viewModel,
                editorCommentsRenderer: this.options.editorCommentsRenderer
            };
        },

        /**
         * Called every time a comment is clicked. Will unselect all widgets (to bring the comment view into focus).
         *
         * @param commentModel
         * @param value
         */
        // eslint-disable-next-line no-unused-vars
        commentViewClickedHandler(commentModel, value) {
            const {viewModel} = this.options;
            // Get the scene ID from the comment
            const sceneId = value.model.sceneID;
            // Get the actual scene from the model
            const scene = viewModel.get("experience").getSceneById(sceneId);
            viewModel.set("sceneNumber", scene.get("sceneNumber"));
            viewModel.selection.clear();
        },

        setCurrentSceneID: function(sceneID) {
            this._currentSceneID = sceneID;
            this.children.each((view) => {
                const isOnCurrentScene = view.getSceneID() === this._currentSceneID;
                view.$el.toggleClass("current-scene", isOnCurrentScene);
            });
        }

    });

    /**
     * Manages the rendering of comments in the editor.
     *
     * <p>Because comments float above the canvas, and can be placed outside the canvas, they can't be rendered inside
     * a container region. Instead, we need to handle the DOM attachment manually. To reduce the complexity of
     * EditorView, this class will handle all of this DOM manipulation.</p>
     *
     * @class EditorCommentsRenderer
     * @constructor
     *
     * @param {jQuery} container The jQuery element to contain the comments.
     * @param {EditorViewModel} viewModel The editor view model.
     */
    return EventDispatcher.extend({

        _container: null,

        _viewScope: null,

        _viewModel: null,

        /**
         * Collection view containing the rendered comments.
         * @type {CommentsView}
         */
        _commentsView: null,

        /**
         * A number that is greater than or equal to the largest z-index of the currently-rendered comments.
         * @type {number}
         */
        _highestCommentZIndex: 0,

        /**
         * How many unpositioned new comments have been added since the collection was reset.
         * @type {number}
         */
        _unpositionedNewCommentsSinceReset: 0,

        constructor: function(container, viewScope) {
            this._container = container;
            this._viewScope = viewScope;
            this._viewModel = viewScope.viewModel;

            var commentCollection = this._viewModel.get("comments");

            this._highestCommentZIndex = this._getHighestIndex(commentCollection);

            // Create the comments view and display it when showComments is active
            this._commentsView = new CommentsView({
                collection: commentCollection,
                viewModel: this._viewModel,
                editorCommentsRenderer: this
            });

            this._commentsView.render();
            this._updateCommentsDisplay();

            this.listenTo(this._viewModel, "change:showComments", this._updateCommentsDisplay);
            this.listenTo(this._viewModel.get("zoomModel"), "change:scale", this._updateToCurrentScaleFactor);

            // Add the comments view to the container
            container.prepend(this._commentsView.$el);

            this._commentsView.trigger("show");

            // Fade the comments layer when a widget is selected
            this.listenTo(this._viewModel.selection, "change:widgets", () => {
                this._commentsView.$el.toggleClass("editor-comments-container-fade", !this._viewModel.selection.isEmpty());
            });

            // Fade comments that are not on the current scene
            this.listenTo(this._viewModel, "change:sceneNumber", function() {
                this._unpositionedNewCommentsSinceReset = 0;
                this._updateToCurrentScene();
            });
            this.listenTo(this._viewModel, "change:currentLayer", () => {
                this._unpositionedNewCommentsSinceReset = 0;
                this._updateToCurrentScene();
            });

            var experience = this._viewModel.get("experience");

            // Listen for experience size changes - making sure comments stick to their scenes
            this.listenTo(experience, "change:width", this._handleExperienceWidthChanges);
            this.listenTo(experience, "change:height", this._handleExperienceHeightChanges);
            // Comments should follow their scenes when scenes are moved
            this.listenTo(experience, "scenes:swapped", this._scenesSwappedHandler);
            // Update view after a comment has been moved/dragged, ensuring potential scrollbars are added/updated
            this.listenTo(this._commentsView, "itemview:moved", this._updateCommentsDisplay);

            this.listenTo(experience, "add:scenes", this._handleSceneAdded);
            this.listenTo(experience, "remove:scenes", this._handleSceneRemoved);

            // Update to current scale when a comment is added
            this.listenTo(commentCollection, "add", this._updateToCurrentScaleFactor);

            this._updateToCurrentScene();
        },

        _getHighestIndex: function(commentCollection) {
            return _.max(commentCollection.map((comment) => {
                return comment.get("zIndex");
            }));
        },

        _updateToCurrentScaleFactor: function() {
            var scale = this._viewModel.get("zoomModel").get("scale");

            this._viewModel.get("comments").each((comment) => {
                comment.set("scaleFactor", scale);
            });

            this._updateCommentsDisplay();
        },

        /**
         * @private
         *
         * Makes sure that all adjacent comments are moved forward when a new scene is added.
         *
         * @param  {Scene} scene The added scene instance.
         */
        _handleSceneAdded: function(scene) {
            var experience = this._viewModel.get("experience"),
                sceneNumber,
                numberOfScenes = experience.getNumberOfScenes();

            for (sceneNumber = scene.get("sceneNumber") + 1; sceneNumber <= numberOfScenes; sceneNumber++) {
                this._moveComments(experience.getSceneBySceneNumber(sceneNumber), "forward");
            }
        },

        /**
         * @private
         *
         * Makes sure that all adjacent comments are moved backwards when a scene is removed.
         *
         * Also, all comments associated with the deleted scene are removed/deleted.
         *
         * @param  {Scene} scene The removed scene instance.
         */
        _handleSceneRemoved: function(scene) {
            var experience = this._viewModel.get("experience"),
                sceneNumber,
                numberOfScenes = experience.getNumberOfScenes();

            for (sceneNumber = scene.get("sceneNumber"); sceneNumber <= numberOfScenes; sceneNumber++) {
                this._moveComments(experience.getSceneBySceneNumber(sceneNumber), "back");
            }

            // Clean up (delete) associated comments
            var comments = this._viewModel.get("comments").getCommentsForScene(scene.get("id"));
            _.invoke(comments, "destroy");
        },

        _handleExperienceWidthChanges(experienceModel, width) {
            const previousWidth = experienceModel.previous("width");

            this._viewModel.get("comments").each((comment) => {
                var scene = experienceModel.getSceneById(comment.sceneID),
                    scenePosition = parseInt(scene.get("sceneNumber"), 10),
                    totalPadding = scenePosition * SCENE_PADDING,
                    previousSceneLeft = (scenePosition * previousWidth) + totalPadding,
                    newSceneLeft = (scenePosition * width) + totalPadding,
                    relativeCommentLeft = comment.get("left") - previousSceneLeft,
                    leftFactor =  relativeCommentLeft / previousWidth,
                    newCommentLeft = newSceneLeft + (width * leftFactor);

                comment.set("left", newCommentLeft);
            });
        },

        _handleExperienceHeightChanges: function(experienceModel, height) {
            var previousHeight = experienceModel.previous("height");

            this._viewModel.get("comments").each((comment) => {
                var topFactor = comment.get("top") / previousHeight;

                comment.set("top", height * topFactor);
            });
        },

        _moveComments: function(scene, direction) {
            var comments = this._viewModel.get("comments").getCommentsForScene(scene.get("id")),
                experienceWidth = this._viewModel.get("experience").get("width"),
                amount = experienceWidth + SCENE_PADDING;

            _.each(comments, (comment) => {
                var commentLeft = comment.get("left");

                comment.set("left", direction === "forward" ? commentLeft + amount : commentLeft - amount, {
                    animate: true
                });
            });
        },

        _scenesSwappedHandler: function(options) {
            var scenes = options.scenes;
            // Scenes sorted by lowest index, i.e. first scene was moved back
            this._moveComments(scenes[0], "back");
            this._moveComments(scenes[1], "forward");
        },

        _updateToCurrentScene: function() {
            var currentScene = this._viewModel.get("scene");

            // Make sure we have a persisted scene ID
            if (currentScene.has("id")) {
                this._commentsView.setCurrentSceneID(currentScene.get("id"));
            } else {
                this.listenToOnce(currentScene, "change:id", function() {
                    this._commentsView.setCurrentSceneID(currentScene.get("id"));
                });
            }
        },

        _updateCommentsDisplay: function() {
            // Show the comments container if needed
            this._commentsView.$el.toggleClass("editor-comments-container-hide", !this._viewModel.get("showComments"));

            this._updateToCurrentScene();
        },

        getCommentBoundingBoxes: function(commentModel) {
            Preconditions.isModel(commentModel);
            return this._commentsView.children.findByModel(commentModel).getBoundingRect();
        },

        /**
         * Moves the given comment to the top, by setting its z-index higher than the current highest index.
         * @param {Comment} comment The comment.
         * @returns {number} The new z-index of the comment.
         */
        moveCommentToTop: function(comment) {
            this._highestCommentZIndex++;
            comment.set("zIndex", this._highestCommentZIndex);
            return this._highestCommentZIndex;
        },

        /**
         * Adds a new comment.
         */
        addComment: function() {
            var options = {},
                containerOffset = this._container.offset(),
                sceneBounds = this._viewScope.getCurrentSceneBounds(),
                scale = this._viewModel.get("zoomModel").get("scale"),
                startLeft = -containerOffset.left + sceneBounds.left + this._unpositionedNewCommentsSinceReset * 15;

            // Position the comment at the top center of the current scene
            options.top = this._unpositionedNewCommentsSinceReset * 15;
            options.left = (startLeft / scale) + (sceneBounds.width * 0.5 - NEW_COMMENT_WIDTH * 0.5);

            this._unpositionedNewCommentsSinceReset++;

            var comment = new Comment(options);

            // Apply correct depth (z-index)
            this.moveCommentToTop(comment);

            this._viewModel.addComment(comment);
        },

        dispose: function() {
            // Clean up
            this.stopListening();
            this._commentsView.close();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/editor/EditorCommentsRenderer.js